<template>
  <div id="activities">
    <AppBatchActionsPanel
      :show="hasSelectedTimeSlots"
      @select-all="selectAllTimeSlots"
      @cancel="resetSelectedTimeSlots"
    >
      <div class="selected-items mr-30">
        {{ selectedTimeSlotsCount }} {{ selectedTimeSlotsCount | pluralize('screen') }} selected
      </div>
      <a
        v-tooltip
        class="all-bin clickable"
        :title="deleteActivitiesTooltip"
        :disabled="preventOwnTimeDeletion"
        data-testid="delete-all-activities-button"
        @click="onDeleteSelectedActivities"
      >
        <i class="hi hi-delete hi-18 text-lighter"></i>
      </a>
      <a
        v-tooltip
        class="all-screens clickable ml-20"
        :data-original-title="deleteScreensTooltip"
        :disabled="!hasSelectedScreens || !deleteScreensAllowed || preventOwnTimeDeletion"
        data-testid="delete-all-screens-button"
        @click="onDeleteSelectedScreens"
      >
        <i class="hi hi-remove-screenshot hi-20 text-lighter"></i>
      </a>
      <a
        v-tooltip
        class="all-move clickable ml-20"
        :data-original-title="moveActivityTooltip"
        :disabled="!canChangeActivityModify"
        @click="showMoveActivityDialog"
      >
        <i class="hi hi-move hi-16 text-lighter"></i>
      </a>
    </AppBatchActionsPanel>
    <ActivitiesLoading v-if="isLoading" />
    <template v-else-if="hasActivities">
      <div class="row">
        <AppGallery
          class="activities-wrapper col-xs-12"
          :gallery-options="{ dynamic: true, thumbnail: false, preload: 1 }"
          :class="{ 'selection-mode': hasSelectedTimeSlots }"
          :fallback-image-url="ACTIVITIES_SCREENSHOT_PENDING_ORIGINAL"
          @gallery-closed="toogleGalleryDialog"
          @gallery-openened="toogleGalleryDialog"
        >
          <ActivitiesHourRow
            v-for="hour in hours"
            :key="hour.starts_at"
            :hour="hour"
            :observer="intersectionObserver"
          />
        </AppGallery>
      </div>
    </template>
    <div
      v-else
      class="no-activity col-xs-12"
    >
      <div class="margin-top text-center">
        <img
          :src="IMAGE_NO_ACTIVITY"
          width="420"
          height="360"
        />
        <h4 class="margin-top">
          There isn't any activity tracked yet for this day. To track time
          <a
            :href="downloadUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            use one of our apps.
          </a>
        </h4>
      </div>
    </div>
    <TimeEntriesOverrideDialog
      v-if="showOverrideDialog"
      multiple
      type="delete"
      :show.sync="showOverrideDialog"
      :affected-records="affectedRecords"
      @submit="confirmOverride"
      @close="close"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { IMAGE_NO_ACTIVITY, ACTIVITIES_SCREENSHOT_PENDING_ORIGINAL } from '~/rails_assets';
import TimeEntriesOverrideDialog from '~/components/time_entries/TimeEntriesOverrideDialog.vue';
import { TimeEntryOverrideApprovedTimesheet } from '~/mixins';

import ActivitiesHourRow from './ActivitiesHourRow.vue';
import ActivitiesLoading from './ActivitiesLoading.vue';

const BODY_LIST_CLASS = 'activities-list';

export default {
  name: 'ActivitiesList',
  components: {
    ActivitiesHourRow,
    ActivitiesLoading,
    TimeEntriesOverrideDialog,
  },
  mixins: [TimeEntryOverrideApprovedTimesheet],
  inject: {
    timesheetScreensOverrideHandler: { default: null },
  },
  data: () => ({
    ACTIVITIES_SCREENSHOT_PENDING_ORIGINAL,
    IMAGE_NO_ACTIVITY,
    intersectionObserver: null,
    showOverrideDialog: false,
    affectedRecords: {},
    validationToken: null,
    previousValidationToken: null,
    /** 'activity' | 'screens' | null – which flow opened the override dialog so confirm dispatches the correct action */
    pendingOverrideType: null,
  }),
  computed: {
    ...mapState({
      isLoading: (state) => state.loading,
      hours: (state) => state.hours,
      permissions: (state) => state.permissions,
      preventOwnTimeDeletion: (state) => state.prevent_own_time_deletion,
      downloadUrl: (state) => state.download_url,
    }),
    ...mapGetters({
      hasActivities: 'hasData',
      hasSelectedTimeSlots: 'hasSelectedTimeSlots',
      selectedTimeSlotsCount: 'selectedTimeSlotsCount',
      selectedTimeSlots: 'selectedTimeSlots',
      selectedTrackedAmount: 'selectedTrackedAmount',
      selectedScreensCount: 'selectedScreensCount',
      selectedProjectsCount: 'selectedProjectsCount',
      hasSelectedScreens: 'hasSelectedScreens',
      queryUserId: 'queryUserId',
      organizationId: 'organizationId',
    }),
    deleteScreensAllowed() {
      return this.permissions.delete_screens_allowed.includes(this.organizationId);
    },
    deleteScreensTooltip() {
      if (this.hasSelectedScreens) {
        if (this.deleteScreensAllowed) {
          return 'Delete all screenshots from selected activities';
        }

        return 'You do not have permission to delete these screenshots';
      }

      return `Selected activities don't have any screenshots`;
    },
    deleteActivitiesTooltip() {
      return 'Delete all selected activities';
    },
    moveActivityTooltip() {
      return this.canChangeActivityModify
        ? 'Move all selected activities'
        : `You do not have permission to move selected activities`;
    },
    canChangeActivityModify() {
      return this.permissions.modify_time_allowed.includes(this.organizationId);
    },
  },
  created() {
    this.intersectionObserver = new IntersectionObserver(this.onEntryObserved, {
      threshold: 0.1,
    });

    window.document.body.classList.add(BODY_LIST_CLASS);
  },
  beforeDestroy() {
    window.document.body.classList.remove(BODY_LIST_CLASS);
  },
  methods: {
    ...mapActions([
      'selectAllTimeSlots',
      'resetSelectedTimeSlots',
      'deleteActivity',
      'deleteScreens',
      'toogleGalleryDialog',
    ]),
    onEntryObserved(entries) {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        const imgs = entry.target.querySelectorAll('img[data-lazy-src]');

        if (imgs?.length) {
          for (const img of imgs) {
            const src = img.getAttribute('data-lazy-src');

            img.removeAttribute('data-lazy-src');
            img.setAttribute('src', src);
          }
        }
      });
    },
    onDeleteSelectedActivities() {
      if (this.preventOwnTimeDeletion) {
        return false;
      }

      const confirmation =
        `Are you sure you want to delete all ${this.selectedTrackedAmount} of activity ` +
        `and ${this.selectedScreensCount} from ${this.selectedProjectsCount}?`;

      if (confirm(confirmation)) {
        this.pendingOverrideType = 'activity';
        this.deleteActivity({ timeSlots: this.selectedTimeSlots }).then(this.success, this.error);
      }
    },
    onDeleteSelectedScreens() {
      if (!this.hasSelectedScreens || !this.deleteScreensAllowed || this.preventOwnTimeDeletion) {
        return false;
      }

      const confirmation =
        `Are you sure you want to delete ${this.selectedScreensCount} ` + `from ${this.selectedProjectsCount}?`;

      if (confirm(confirmation)) {
        this.pendingOverrideType = 'screens';
        this.deleteScreens({ timeSlots: this.selectedTimeSlots }).then(this.success, this.error);
      }
    },
    showMoveActivityDialog() {
      if (!this.canChangeActivityModify) {
        return false;
      }
      const formattedTimeSlots = this.selectedTimeSlots.map((ts) => `&time_slots[]=${ts}`).join('');
      const urlParams =
        `?organization_id=${this.organizationId}` + `&user_id=${this.queryUserId}` + `${formattedTimeSlots}`;

      AjaxDialog.run(`/activities/move_activity_dialog.dialog${urlParams}`);
    },
    error() {
      this.$appMessage('Something went wrong. Please try again.');
    },
    success(response) {
      if (response?.action === 'confirm-override') {
        if (this.timesheetScreensOverrideHandler) {
          this.timesheetScreensOverrideHandler(response, (token, timesheetRedirectUrl) =>
            this.confirmOverrideWithToken(token, timesheetRedirectUrl)
          );

          return;
        }
        this.affectedRecords = response.associated_records || {};
        this.previousValidationToken = this.validationToken;
        this.validationToken = response.validation_token;
        this.timesheetRedirectUrl = response.timesheet_redirect_url;
        this.showOverrideDialog = true;

        return;
      }

      this.resetSelectedTimeSlots();
      const message =
        response?.message ??
        (response?.success ? 'Operation successful' : 'Something went wrong. Please try again.');

      this.$appMessage(message);

      if (response?.success && (this.timesheetRedirectUrl || response?.timesheet_redirect_url)) {
        if (response?.timesheet_redirect_url) {
          this.timesheetRedirectUrl = response.timesheet_redirect_url;
        }
        this.redirectToTimesheet();
      }
    },
    confirmOverride() {
      this.showOverrideDialog = false;
      const payload = {
        timeSlots: this.selectedTimeSlots,
        confirmed: true,
        validationToken: this.validationToken,
      };
      const overrideType = this.pendingOverrideType;

      this.pendingOverrideType = null;

      if (overrideType === 'screens') {
        this.deleteScreens(payload).then(this.success, this.error);
      } else {
        this.deleteActivity(payload).then(this.success, this.error);
      }
    },
    /** Used when parent (e.g. timesheet dialog) handles the override UI: set token/url then use same confirm flow as local dialog for action-tracking. */
    confirmOverrideWithToken(token, timesheetRedirectUrl) {
      if (timesheetRedirectUrl) {
        this.timesheetRedirectUrl = timesheetRedirectUrl;
      }
      this.validationToken = token;
      this.confirmOverride();
    },
    performOverrideConfirm(token, timesheetRedirectUrl) {
      if (timesheetRedirectUrl) {
        this.timesheetRedirectUrl = timesheetRedirectUrl;
      }
      const payload = {
        timeSlots: this.selectedTimeSlots,
        confirmed: true,
        validationToken: token,
      };
      const overrideType = this.pendingOverrideType;

      this.pendingOverrideType = null;

      if (overrideType === 'screens') {
        this.deleteScreens(payload).then(this.success, this.error);
      } else {
        this.deleteActivity(payload).then(this.success, this.error);
      }
    },
    close() {
      this.showOverrideDialog = false;
      this.affectedRecords = {};
      this.validationToken = null;
      this.timesheetRedirectUrl = null;
      this.pendingOverrideType = null;
      this.resetSelectedTimeSlots();
    },
  },
};
</script>
